import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, Check, ChevronDown, Pencil, NotebookPen, Flame, Trophy } from "lucide-react";
import { motion } from "framer-motion";
import { api, formatApiError } from "@/lib/api";
import { QuickAddHomework, useSubjects } from "@/components/QuickAddHomework";
import { subjectStyle, ukToday, plusDays, fmtDue, daysUntil } from "@/lib/subjects";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { RevisionPlan } from "@/components/RevisionPlan";
import { AdminTag } from "@/components/ActAs";
import { useEdition } from "@/lib/edition";

const GROUPS = [
  ["overdue", "Overdue", "text-rose-600", (d, t) => d < t],
  ["today", "Due today", "text-brand-dark", (d, t) => d === t],
  ["tomorrow", "Tomorrow", "text-amber-700", (d) => d === plusDays(1)],
  ["week", "This week", "text-slate-600", (d) => d > plusDays(1) && d <= plusDays(7)],
  ["later", "Later", "text-slate-500", (d) => d > plusDays(7)],
];

const TEST_GROUPS = [
  ["soon", "Next 7 days", "text-brand-dark", (d, t) => d >= t && d <= plusDays(7)],
  ["later", "Later", "text-blue-700", (d) => d > plusDays(7)],
];

const countdown = (d) => { const n = daysUntil(d); return n === 0 ? "Today" : n === 1 ? "Tomorrow" : n > 1 ? `in ${n} days` : "Past"; };

const SubjectChip = ({ s }) => s ? <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${subjectStyle(s).chip}`}>{s}</span> : null;

const Item = ({ h, onToggle, onEdit, onDelete, onChange }) => (
  <motion.li layout initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} data-testid={`homework-item-${h.hw_id}`}
    className="group relative flex items-start gap-3 rounded-2xl bg-surface border border-slate-200/80 p-4 pl-5 shadow-sm hover:shadow-md transition-shadow overflow-hidden">
    <span className="absolute left-0 top-0 bottom-0 w-1.5" style={{ backgroundColor: subjectStyle(h.subject).dot }} />
    <button data-testid={`homework-toggle-${h.hw_id}`} onClick={() => onToggle(h)} aria-label={h.done ? "Mark not done" : "Mark done"}
      className={`mt-0.5 grid place-items-center w-6 h-6 shrink-0 rounded-lg border-2 transition-colors ${h.done ? "bg-emerald-600 border-emerald-600 text-white" : "border-slate-300 hover:border-brand"}`}>
      {h.done && <Check className="w-4 h-4" />}
    </button>
    <div className="flex-1 min-w-0">
    <button onClick={() => onEdit(h)} data-testid={`homework-open-${h.hw_id}`} className="w-full text-left">
      <p className={`font-semibold text-[15px] leading-snug ${h.done ? "line-through text-slate-400" : "text-slate-900"}`}>{h.title}</p>
      <div className="mt-1.5 flex flex-wrap items-center gap-2">
        <SubjectChip s={h.subject} />
        <span className="font-mono text-[11px] text-slate-500">{fmtDue(h.due)}</span>
        {h.kind === "test" && !h.done && <span data-testid={`test-countdown-${h.hw_id}`} className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${daysUntil(h.due) <= 1 ? "bg-brand text-white" : "bg-blue-600 text-white"}`}>{countdown(h.due)}</span>}
        {h.kind === "test" && h.score != null && h.out_of ? <span data-testid={`test-score-${h.hw_id}`} className="rounded-full bg-gold-400 px-2 py-0.5 text-[11px] font-bold text-black">{h.score}/{h.out_of}</span> : null}
        {h.notes ? <NotebookPen className="w-3.5 h-3.5 text-slate-400" /> : null}
        <AdminTag by={h.added_by_admin} id={`hw-admin-tag-${h.hw_id}`} />
      </div>
    </button>
    {h.kind === "test" && !h.done && daysUntil(h.due) >= 0 && <RevisionPlan test={h} onChange={onChange} />}
    </div>
    <button data-testid={`homework-edit-${h.hw_id}`} onClick={() => onEdit(h)} aria-label="Edit" className="grid place-items-center w-9 h-9 rounded-xl text-slate-500 hover:bg-slate-100 transition-colors"><Pencil className="w-4 h-4" /></button>
    <button data-testid={`homework-delete-${h.hw_id}`} onClick={() => onDelete(h)} aria-label="Delete" className="grid place-items-center w-9 h-9 rounded-xl text-slate-500 hover:bg-rose-600 hover:text-white transition-colors"><Trash2 className="w-4 h-4" /></button>
  </motion.li>
);

const EditDialog = ({ h, subs, onClose, onSaved }) => {
  const [f, setF] = useState(h);
  useEffect(() => { setF(h); }, [h]);
  if (!h) return null;
  const save = async (e) => {
    e.preventDefault();
    try { await api.put(`/homework/${h.hw_id}`, { title: f.title, subject: f.subject ?? "", due: f.due, notes: f.notes ?? "", done: f.done, kind: f.kind ?? "homework", ...(f.kind === "test" ? { score: f.score === "" || f.score == null ? null : Number(f.score), out_of: f.out_of === "" || f.out_of == null ? null : Number(f.out_of) } : {}) }); toast.success("Saved"); onSaved(); onClose(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const cls = "w-full rounded-xl border border-slate-200 bg-surface px-4 py-2.5 text-base text-slate-900 outline-none focus:border-brand focus:ring-2 focus:ring-brand/20";
  return (
    <Dialog open={!!h} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="homework-edit-dialog" className="rounded-3xl max-w-[calc(100vw-2rem)] sm:max-w-lg">
        <DialogHeader><DialogTitle className="font-display text-3xl font-normal">{f?.kind === "test" ? "Edit test" : "Edit homework"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <input data-testid="edit-title" required value={f?.title ?? ""} onChange={(e) => setF({ ...f, title: e.target.value })} className={cls} />
          <div className="grid grid-cols-2 gap-2">
            <select data-testid="edit-subject" value={f?.subject ?? ""} onChange={(e) => setF({ ...f, subject: e.target.value })} className={cls}>
              <option value="">No subject</option>
              {[...new Set([...(subs.map((s) => s.subject)), f?.subject].filter(Boolean))].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <input data-testid="edit-due" type="date" required value={f?.due ?? ""} onChange={(e) => setF({ ...f, due: e.target.value })} className={cls} />
          </div>
          <textarea data-testid="edit-notes" rows={4} value={f?.notes ?? ""} onChange={(e) => setF({ ...f, notes: e.target.value })} placeholder={f?.kind === "test" ? "Topics to revise, pages, what to bring…" : "Notes: pages, questions, what the teacher said…"} className={cls} />
          {f?.kind === "test" && (
            <div className="grid grid-cols-2 gap-2">
              <input data-testid="edit-score" type="number" min="0" step="any" placeholder="Your score" value={f?.score ?? ""} onChange={(e) => setF({ ...f, score: e.target.value })} className={cls} />
              <input data-testid="edit-out-of" type="number" min="1" step="any" placeholder="Out of" value={f?.out_of ?? ""} onChange={(e) => setF({ ...f, out_of: e.target.value })} className={cls} />
            </div>
          )}
          <button data-testid="edit-save" className="w-full rounded-xl bg-brand px-5 py-3 font-semibold text-white hover:bg-brand-dark active:scale-[0.98] transition-[background-color,transform]">Save</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const TestResults = ({ items }) => {
  const scored = items.filter((h) => h.kind === "test" && h.score != null && h.out_of);
  if (!scored.length) return <p data-testid="test-results-empty" className="text-sm text-slate-500">After a test, tap it and add your score to track results.</p>;
  const by = {};
  scored.forEach((h) => { const k = h.subject || "Other"; (by[k] = by[k] || []).push((h.score / h.out_of) * 100); });
  const rows = Object.entries(by).map(([s, v]) => [s, Math.round(v.reduce((a, b) => a + b, 0) / v.length), v.length]).sort((a, b) => b[1] - a[1]);
  return (
    <section data-testid="test-results" className="rounded-3xl border border-slate-200 bg-surface p-5">
      <h2 className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.18em] font-bold text-gold-300"><Trophy className="w-4 h-4" />Results by subject</h2>
      <ul className="mt-3 space-y-2">
        {rows.map(([s, pct, n]) => (
          <li key={s} data-testid={`result-${s.toLowerCase().replace(/\s+/g, "-")}`} className="text-sm">
            <div className="flex justify-between"><span className="font-semibold text-slate-900">{s}</span><span className="font-mono text-slate-600">{pct}% · {n} test{n > 1 ? "s" : ""}</span></div>
            <div className="mt-1 h-2 rounded-full bg-slate-200 overflow-hidden"><div className="h-full" style={{ width: `${Math.min(100, pct)}%`, backgroundColor: subjectStyle(s).dot }} /></div>
          </li>
        ))}
      </ul>
    </section>
  );
};

export default function Homework() {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("");
  const [showDone, setShowDone] = useState(false);
  const [editing, setEditing] = useState(null);
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "tests" ? "test" : "homework";
  const isTest = tab === "test";
  const { corp, w } = useEdition();
  const subs = useSubjects();
  const load = () => api.get("/homework").then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch(() => setItems([]));
  const [streak, setStreak] = useState(null);
  const loadStreak = () => api.get("/streak").then((r) => setStreak(r.data ?? null)).catch(() => {});
  useEffect(() => { loadStreak(); }, []);
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!(items ?? []).some((h) => h.plan_status === "running")) return undefined;
    const timer = setTimeout(load, 4000);
    return () => clearTimeout(timer);
  }, [items]);

  const toggle = async (h) => {
    await api.put(`/homework/${h.hw_id}`, { title: h.title, subject: h.subject ?? "", due: h.due, notes: h.notes ?? "", done: !h.done, kind: h.kind ?? "homework" });
    const { data } = await api.get("/homework");
    const list = Array.isArray(data) ? data : [];
    setItems(list);
    if (!h.done) {
      const t0 = ukToday();
      const dueToday = list.filter((x) => x.kind !== "test" && x.due <= t0);
      if (h.kind !== "test" && h.due <= t0 && dueToday.every((x) => x.done)) toast.success("All caught up for today. Streak kept!", { icon: <Flame className="w-4 h-4 text-brand" /> });
      else toast.success(h.kind === "test" ? "Test marked done" : "Nice one. Done!");
    }
    loadStreak();
  };
  const del = async (h) => { await api.delete(`/homework/${h.hw_id}`); toast.success("Deleted"); load(); };

  const t = ukToday();
  const all = (items ?? []).filter((h) => (h.kind ?? "homework") === tab);
  const shown = all.filter((h) => !filter || h.subject === filter);
  const open = shown.filter((h) => !h.done && !(isTest && h.due < t));
  const done = shown.filter((h) => h.done || (isTest && h.due < t));
  const usedSubjects = useMemo(() => [...new Set(all.filter((h) => !h.done && h.subject).map((h) => h.subject))], [all]);
  const counts = isTest
    ? [["This week", open.filter((h) => h.due <= plusDays(7)).length, "text-brand-dark"], ["Tomorrow", open.filter((h) => h.due === plusDays(1)).length, "text-brand-dark"], ["Later", open.filter((h) => h.due > plusDays(7)).length, "text-blue-700"]]
    : [["Overdue", open.filter((h) => h.due < t).length, "text-rose-600"], ["Today", open.filter((h) => h.due === t).length, "text-brand-dark"], ["Tomorrow", open.filter((h) => h.due === plusDays(1)).length, "text-amber-700"]];
  const switchTab = (k) => { setFilter(""); setShowDone(false); setParams(k === "test" ? { tab: "tests" } : {}); };

  return (
    <main data-testid="homework-page" className="mx-auto max-w-3xl px-4 sm:px-6 py-8 sm:py-12 space-y-6">
      <div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="font-mono text-xs uppercase tracking-[0.2em] font-bold text-teal-700">{w("Schoolwork", "Work")}</p>
          <div data-testid="homework-tabs" className="inline-flex rounded-2xl bg-slate-100 p-1">
            {[["homework", w("Homework", "Tasks")], ["test", w("Tests", "Deadlines")]].map(([k, l]) => (
              <button key={k} data-testid={`tab-${k}`} onClick={() => switchTab(k)} className={`rounded-xl px-4 py-2 text-sm font-semibold transition-colors ${tab === k ? "bg-navy-900 text-white" : "text-slate-600 hover:text-slate-900"}`}>{l}</button>
            ))}
          </div>
        </div>
        <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl text-slate-900 leading-none">{open.length ? `${open.length} ${isTest ? w(`test${open.length > 1 ? "s" : ""} coming up`, `deadline${open.length > 1 ? "s" : ""} coming up`) : `thing${open.length > 1 ? "s" : ""} to do`}` : isTest ? w("No tests coming up", "No deadlines coming up") : "All clear"}</h1>
        <div data-testid="homework-counts" className="mt-4 grid grid-cols-3 gap-2">
          {counts.map(([l, n, c]) => (
            <div key={l} className="rounded-2xl bg-surface border border-slate-200 p-3"><p className={`font-display text-3xl leading-none ${n ? c : "text-slate-300"}`}>{n}</p><p className="mt-1 text-xs font-semibold text-slate-600">{l}</p></div>
          ))}
        </div>
      </div>
      {streak && !isTest && (
        <p data-testid="homework-streak" className="inline-flex items-center gap-2 rounded-full border border-brand/40 bg-red-50 px-3 py-1.5 text-sm font-semibold text-slate-900">
          <Flame className="w-4 h-4 text-brand" />{streak.streak}-day streak<span className="text-slate-500 font-normal">· best {streak.best}</span>
        </p>
      )}
      {isTest && !corp && <TestResults items={items ?? []} />}
      <QuickAddHomework onAdded={load} defaultKind={tab} />
      {usedSubjects.length > 1 && (
        <div data-testid="homework-filters" className="-mx-4 px-4 flex gap-2 overflow-x-auto pb-1">
          <button data-testid="filter-all" onClick={() => setFilter("")} className={`h-9 shrink-0 rounded-full px-4 text-xs font-semibold border transition-colors ${!filter ? "bg-slate-900 text-black border-slate-900" : "bg-surface text-slate-700 border-slate-200"}`}>All</button>
          {usedSubjects.map((s) => (
            <button key={s} data-testid={`filter-${s.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => setFilter(filter === s ? "" : s)}
              className={`h-9 shrink-0 rounded-full px-4 text-xs font-semibold border transition-colors ${filter === s ? "bg-slate-900 text-black border-slate-900" : subjectStyle(s).chip}`}>{s}</button>
          ))}
        </div>
      )}
      {items === null && <div className="h-20 rounded-2xl bg-surface/70 animate-pulse" />}
      {all.length === 0 && items !== null && <p data-testid="homework-empty" className="rounded-3xl border-2 border-dashed border-slate-300 p-10 text-center text-slate-600">{isTest ? w("No tests yet. Add one above so Daybreyk can remind you to revise.", "No deadlines yet. Add one above so Daybreyk can remind you to prepare.") : w("No homework yet. Type it in the box above.", "No tasks yet. Type one in the box above.")}</p>}
      {(isTest ? TEST_GROUPS : GROUPS).map(([key, label, color, test]) => {
        const list = open.filter((h) => test(h.due, t));
        if (!list.length) return null;
        return (
          <section key={key} data-testid={`homework-group-${key}`}>
            <h2 className={`mb-3 flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] font-bold ${color}`}>{label}<span className="rounded-full bg-surface border border-slate-200 px-2 py-0.5 text-slate-600">{list.length}</span></h2>
            <ul className="space-y-2.5">{list.map((h) => <Item key={h.hw_id} h={h} onToggle={toggle} onEdit={setEditing} onDelete={del} onChange={load} />)}</ul>
          </section>
        );
      })}
      {done.length > 0 && (
        <section data-testid="homework-group-done">
          <button data-testid="toggle-done-section" onClick={() => setShowDone(!showDone)} className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] font-bold text-emerald-700">
            {isTest ? "Past & done" : "Done"}<span className="rounded-full bg-surface border border-slate-200 px-2 py-0.5 text-slate-600">{done.length}</span><ChevronDown className={`w-4 h-4 transition-transform ${showDone ? "rotate-180" : ""}`} />
          </button>
          {showDone && <ul className="mt-3 space-y-2.5">{done.map((h) => <Item key={h.hw_id} h={h} onToggle={toggle} onEdit={setEditing} onDelete={del} onChange={load} />)}</ul>}
        </section>
      )}
      <EditDialog h={editing} subs={subs} onClose={() => setEditing(null)} onSaved={load} />
    </main>
  );
}
